import React, { useEffect, useState } from 'react';
import Grid from "@mui/material/Grid";
import {getGroupData, getToken, groupTypes, yearList, simpleFetch, fetchById, fetchData} from "../../../utils/helpsers";
import useQuery from "../../../utils/userLocation";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from "@mui/material/Button";
import { useAuth0 } from "@auth0/auth0-react";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import PropTypes from "prop-types";
import Box from "@mui/material/Box"
import { BoardBuilderStyles } from './BoardBuilder.style';

/**
 * Board Builder Component.comparisonYear
 * @param {boolean} groupEnabled -TRUE if group select field is required.
 * @param {boolean} yearEnabled - TRUE if yard select field is required.
 * @param {function} boardData - Hook to be feed with the board builder data collected.
 * @param {function} onSubmit - Function to be executed on submit.
 * @param {object} existingBoard - Default data if a saved board is loaded.
 * @param {string || null} type - A kye definition related with the board the component is used.
 */

function BoardBuilder({ groupEnabled = false, yearEnabled = false, boardData, onSubmit, existingBoard, type = null }) {
    // Hook to feed select fields.
    const [districts, setDistricts] = useState([]);
    const [groups, setGroups] = useState([]);

    // Hooks to host the board data to be returned.
    const [referenceDistrict, setReferenceDistrict] = useState(null);
    const [groupType, setGroupType] = useState(null);
    const [comparisonGroup, setComparisonGroup] = useState(null);
    const [irns, setIrns] = useState();
    const [year, setYear] = useState(null);
    const [groupCount, setGroupCount] = useState();

    const [fixedSelection, setFixedSelection] = useState(false);
    const { user } = useAuth0();

    let rootClass = "board-builder";
    let query = useQuery();

    // Set districts allowed based on storage data.
    useEffect(()=> {
        if (localStorage.getItem('allowed_districts')) {
            let districts = JSON.parse(localStorage.allowed_districts);
            setDistricts(districts);
            let conditionalDistrict = user[`${process.env.REACT_APP_AUTH0_KEY}/district`].sessionType === 'district-access' ? districts : districts[0];
            setReferenceDistrict(conditionalDistrict)
        } else {
            // No district saved on storage, request district.
            let userEmail = encodeURIComponent(user.email);
            simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_allowed_districts?_email=${userEmail}`)
            .then(data => {
                let conditionalDistrict;
                if(user[`${process.env.REACT_APP_AUTH0_KEY}/district`].sessionType === 'district-access') {
                    localStorage.setItem('allowed_districts', JSON.stringify(data[0]));
                    conditionalDistrict = [data[0]];
                } else {
                    localStorage.setItem('allowed_districts', JSON.stringify(data[0].sort(function(a, b) {
                        return a.label.localeCompare(b.label);
                    })));
                    conditionalDistrict = data[0];
                }

                setDistricts(conditionalDistrict);
                setReferenceDistrict(conditionalDistrict);
            });
        }
    },[user])

    // If existing board or fixed configs, set board filters.
    useEffect(() => {
        if (existingBoard){
            simpleFetch(`${process.env.REACT_APP_API_URL}/custom_boards?uuid=eq.${query.get('shared')}`)
                .then(data => {
                    let savedData = Object.values(data[0].data)[0]
                    setReferenceDistrict(savedData.referenceDistrict);
                    setGroupType(savedData.groupType);
                    setComparisonGroup(savedData.groupInfo);
                    setYear(savedData.year);
                    setFixedSelection(false);
                })
        }
       else if (localStorage.getItem('fixedSelection') && !existingBoard) {
            let fixedSelection = JSON.parse(localStorage.fixedSelection);
            setReferenceDistrict(fixedSelection?.referenceDistrict);
            setGroupType(fixedSelection?.groupType);
            setComparisonGroup(fixedSelection?.groupInfo);
            setYear(fixedSelection?.year);
            setFixedSelection(true);
       }
    }, [user, existingBoard, query])

    // Get comparison groups.
    useEffect(() => {
        if (referenceDistrict?.value && groupType?.value) {
            let groupId = groupType.value;

            if (groupId === 'All') {
                setComparisonGroup({ label: "All Ohio School Districts", value: 1 });
            } else if (groupId === 'custom_groups') {
                // @TODO create a sql function to fetch only the custom groups related to the current user.
                simpleFetch(`${process.env.REACT_APP_API_URL}/custom_groups`, getToken())
                    .then((customGroups) => {
                        const availableGroups = customGroups.filter(group => {
                            let valid = group['created_by'] === user.email;
                            if (group['available_users_share']) {
                                if (group['available_users_share'].filter(userAvailable => userAvailable.value === user.email).length > 0) {
                                    valid = true;
                                }
                            }
                            return valid;
                        });
                        setGroups(availableGroups);
                    });
            } else {
                // Data to fill group selector field only if not existing group.
                getGroupData(`${process.env.REACT_APP_API_URL}/rpc/hvd_group_name?_irn=` + referenceDistrict.value + "&_reference=" + referenceDistrict.label + "&_group_type=", groupId, setGroups, getToken());

            }
        }
    }, [referenceDistrict?.value, groupType?.value, user, existingBoard, referenceDistrict?.label])


    useEffect(() => {
        const fetchController = new AbortController();
        if (comparisonGroup) {
            let groupId = comparisonGroup.label;
            let urlGroupSelected = `${process.env.REACT_APP_API_URL}/hvd_group_selected?select=count,label,value,irns&label=eq.`;
            if (groupType.value === 'Enrollment+Brackets') {
                groupId = comparisonGroup.value;
                urlGroupSelected = `${process.env.REACT_APP_API_URL}/hvd_group_selected?select=count,label,value,irns&value=eq.`;
            }

            // If comparison group selected is equal to Custom Group
            if (groupType.value === 'custom_groups' && comparisonGroup.custom_ranges) {
                let varKey = (Object.keys(comparisonGroup.custom_ranges).length);
                let varData = comparisonGroup.custom_ranges[`var${varKey}`];
                setIrns([...varData.irns]);
            // If comparison group selected is equal to Custom Boards
            } else if (groupType.value === 'custom_boards') {
                setIrns(comparisonGroup.data.irns);
                setYear(comparisonGroup.data.year);
            } else if (['County','District+Picker'].includes(groupType.value)) {
                let urls = [];
                let irnList;
                // Build fetch url for each group selected.
                comparisonGroup.forEach( group => {
                    urls.push(`${urlGroupSelected}${group.label}`);
                })

                // Fetching and merging the irns.
                Promise.all(urls.map(url =>
                    fetchData({
                        endpoint: url,
                        controller: fetchController
                    }).then( res => {
                        irnList = irnList ? `${irnList}, ${res.data[0].irns}` : res.data[0].irns;
                    })
                )).then( data => { setIrns(irnList)})
            }
            else {
               // Similarity and All Ohi group types.
              // @TODO BackEnd must update the group ID label to remove the 's.
              groupId = groupId === "All Ohio School Districts" ? "All Ohio's School Districts" : groupId;
              fetchById(urlGroupSelected, groupId, getToken())
                  .then(data => {
                    if (data[0]) {
                      setIrns(data[0].irns);
                      // setGroupCount(data[0].count)
                    }
                  });
            }
        }
    }, [groups, groupType, comparisonGroup]);


    // Using IRN string to get group count.
    useEffect(()=> {
        if(irns) {
            let irnsToString = (irns).toString()
            let irnsSplit = irnsToString.split(',')
            let count = irnsSplit.length;
            setGroupCount(count);
        }
    },[irns])


    // Data stored by the board builder fields.
    const builderData = {
        referenceDistrict,
        irns,
        groupType,
        groupCount,
        groupInfo: comparisonGroup,
        year,
    };

    const fixSelectionControl = (event) => {
        setFixedSelection(event.target.checked);
        if (event.target.checked) {
            return localStorage.setItem('fixedSelection', JSON.stringify(builderData))
        } else  {
            return localStorage.removeItem('fixedSelection')
        }
    };

    return (
      <Box sx={BoardBuilderStyles({groupEnabled})}>
        <form
          className={rootClass}
          onSubmit={(e) => {
            e.preventDefault();
            fixedSelection && localStorage.setItem("fixedSelection", JSON.stringify(builderData));
            // boardData && boardData(builderData);
            if(boardData) {
              // Time required to clean the previous data context before update.
              setTimeout(()=> boardData(builderData), 1000)
            }
            onSubmit && onSubmit();
          }}
        >
          <Grid container rowSpacing={2} columnSpacing={0}>
            <Grid item xs={12}>
              <FormGroup className={"fix-selection-switch"}>
                <FormControlLabel
                  disabled={!!existingBoard}
                  control={
                    <Switch color="purpleCold" checked={fixedSelection} onChange={fixSelectionControl} />
                  }
                  label="Fix Selection"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              {districts.length > 1 ? (
                <Autocomplete
                  id="referenceDistrict"
                  value={referenceDistrict}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  options={districts}
                  fullWidth={true}
                  onKeyDown={e => e.stopPropagation()}
                  onChange={(event, newValue) => {
                    setReferenceDistrict(newValue);
                    // Matomo code.
                    if (newValue) {
                      window._paq.push([
                        "trackEvent",
                        "Board InsightBoard",
                        "Clicked: Reference Distric",
                        `Selected: ${newValue.label}`,
                      ]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Reference district"
                      required={true}
                      
                    />
                  )}
                />
              ) : (
                <>
                  <div>{referenceDistrict ? referenceDistrict.label : "Loading"}</div>
                </>
              )}
            </Grid>
            {(groupEnabled && referenceDistrict) && (
                <Grid item xs={12}>
                  <Grid container columnSpacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        id="groupType"
                        value={groupType}
                        isOptionEqualToValue={(option, value) =>
                          option.value === value.value
                        }
                        options={groupTypes}
                        fullWidth={true}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            // Reset group and irns to avoid undefined values.
                            setComparisonGroup(null)
                            setIrns(null)
                            // Clean the hook before updated the selected group type.
                            setGroupType(null);
                            setTimeout(() => setGroupType(newValue), 100)
                            // Matomo code.
                            window._paq?.push([
                              "trackEvent",
                              "Board InsightBoard",
                              "Clicked: Comparison group",
                              `Selected: ${newValue.label}`,
                            ]);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Comparison group"
                            required={true}
                          />
                        )}
                      />
                    </Grid>
                    {groupType && groupType.value !== "All" && (
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple={['County','District+Picker'].includes(groupType.value)}
                          id="comparisonGroup"
                          className={'comparison-group-field'}
                          value={
                            // The Autocomplete expects the `value` prop to be an array when `multiple={true}`.
                            ['County','District+Picker'].includes(groupType.value)
                              ? (Array.isArray(comparisonGroup) ? comparisonGroup : [])
                              : comparisonGroup
                            }
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          sx={{ marginTop: {xs: "10px", sm: 0}}}
                          noOptionsText={'Loading...'}
                          onKeyDown={e => e.stopPropagation()}
                          options={groups}
                          fullWidth={true}
                          onChange={(event, newValue) => {
                            setComparisonGroup(newValue);

                            if (newValue) {
                              window._paq.push([
                                "trackEvent",
                                "Board InsightBoard",
                                `Clicked: ${groupType.label}`,
                                `Selected: ${newValue.label}`,
                              ]);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={groupType.label}
                              // Multiple can't pass 'required' validation. See https://github.com/mui/material-ui/issues/21663
                              required={['County','District+Picker'].includes(groupType.value) ? comparisonGroup?.length === 0 : true }
                            />
                          )}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            { yearEnabled && (
              <Grid item xs={12}>
                <Autocomplete
                  id="year"
                  value={year}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  options={ yearList }
                  fullWidth={true}
                  onKeyDown={e => e.stopPropagation()}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setYear(newValue);
                      window._paq?.push([
                        "trackEvent",
                        "Board InsightBoard",
                        "Clicked: Year",
                        `Selected: ${newValue.label}`,
                      ]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={"Year"} required={true} />
                  )}
                />
              </Grid>
            )}
          </Grid>
          <div className={"actions"}>
            {
              // Submit validation only if required values are ready to be set.
              (referenceDistrict && ( !groupEnabled || irns ) && (!yearEnabled || year)) && (
                <Button className={"submit-button"}
                  onClick={() => window._paq.push(["trackGoal", 6])}
                  variant="contained"
                  type="submit"
                >
                  Build Board
                </Button>
              )
            }
          </div>
        </form>
      </Box>
    );
}

BoardBuilder.propTypes = {
 groupEnabled: PropTypes.bool,
 yearEnabled: PropTypes.bool.isRequired,
 boardData: PropTypes.func.isRequired,
 onSubmit: PropTypes.func,
 existingBoard: PropTypes.object,
}

export default BoardBuilder;
